import Guid from "common/values/guid/guid";
import moment, { Moment } from "moment";
import UserNetworkConnectionProfile from "users/entities/user-network-connection/user-network-connection-profile";
import UserProfile, { UserProfileJSON } from "users/values/user-profile/user-profile";

export interface UserNetworkConnectionJSON {
  id: string;
  createdDate: string;
  userA: UserProfileJSON;
  userB: UserProfileJSON;
}

export default class UserNetworkConnection {
  public readonly id: Guid;
  public readonly createdDate: Moment;
  public readonly userA: UserProfile;
  public readonly userB: UserProfile;

  constructor(
    id: Guid,
    createdDate: Moment,
    userA: UserProfile,
    userB: UserProfile
  ) {
    this.id = id;
    this.createdDate = createdDate;
    this.userA = userA;
    this.userB = userB;
  }

  public toProfileConnection(localUserId?: Guid): UserNetworkConnectionProfile {
    if (!localUserId)
      throw new InvalidUserNetworkConnectionError({
        localUserId: 'Local user id is required to create connection profile'
      });
    const otherUserProfile = this.userA.id !== localUserId ? this.userA : this.userB;
    return new UserNetworkConnectionProfile(
      this.id,
      otherUserProfile,
      this.createdDate
    );
  }

  public static fromJSON(connection: UserNetworkConnectionJSON): UserNetworkConnection {
    if (!connection.id)
      throw new InvalidUserNetworkConnectionError({ id: 'Id is required.' });
    if (!connection.userA)
      throw new InvalidUserNetworkConnectionError({ userA: 'User A is required.' });
    if (!connection.userB)
      throw new InvalidUserNetworkConnectionError({ userB: 'User B is required.' });

    const id = Guid.fromJSON(connection.id);
    if (!id)
      throw new InvalidUserNetworkConnectionError({ id: 'Invalid id.' });

    return new UserNetworkConnection(
      id,
      moment(connection.createdDate),
      UserProfile.fromJSON(connection.userA),
      UserProfile.fromJSON(connection.userB)
    );
  }

  public toJSON(): UserNetworkConnectionJSON {
    return {
      id: this.id.toString(),
      createdDate: this.createdDate.toISOString(),
      userA: this.userA.toJSON(),
      userB: this.userB.toJSON()
    };
  }
}

export class InvalidUserNetworkConnectionError extends Error {
  issues: unknown;

  constructor(issues: unknown) {
    super(`Invalid user network connection.`);
    this.issues = issues;
  }
}
